import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { MenuItem, TextField } from '@mui/material'
import React from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import type { Genero } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { useSellerFormModel } from './seller-form-model'
import type { SellerFormProps } from './seller-form-model'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const SellerForm = (props: SellerFormProps) => {
    const model = useSellerFormModel(props)
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} modal={props.modal} title={model.title}>
            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.loading && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description={model.isEdit ? 'Actualiza los datos del vendedor en tu cartera.' : 'Captura rápida: solo los datos necesarios para registrarlo en tu cartera.'}
                    backTo={model.backTo}
                    backLabel="Vendedores"
                    actions={<span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Datos generales</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField className="v2-field" variant="filled" fullWidth required label="Nombre(s)" value={form.nombres} onChange={(event) => change.nombres(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth required label="Apellido paterno" value={form.apPat} onChange={(event) => change.apPat(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth required label="Apellido materno" value={form.apMat} onChange={(event) => change.apMat(event.target.value)} />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            label="Sexo"
                            value={form.sexo}
                            onChange={(event) => change.sexo(event.target.value as Genero | '')}
                            slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                        >
                            <MenuItem value=""><em>Seleccione</em></MenuItem>
                            <MenuItem value="hombre">Hombre</MenuItem>
                            <MenuItem value="mujer">Mujer</MenuItem>
                        </TextField>
                        <TextField
                            className="v2-field" variant="filled" fullWidth
                            required
                            label="RFC"
                            value={form.rfc}
                            onChange={(event) => change.rfc(event.target.value)}
                        />
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Localización</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField
                            className="v2-field md:col-span-2" variant="filled" fullWidth
                            required
                            type="email"
                            label="Correo"
                            value={form.correo}
                            onChange={(event) => change.correo(event.target.value)}
                        />
                        <div className="v2-phone">
                            <span className="v2-phone-label">Teléfono<span>*</span></span>
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                disableDropdown
                                specialLabel=""
                                value={form.telefono}
                                onChange={(value) => change.telefono(value)}
                                inputProps={{ required: true, name: 'telefono' }}
                            />
                            <p className="v2-phone-hint">Número con el que se localiza al vendedor.</p>
                        </div>
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.loading}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar vendedor
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default SellerForm
